import { selectCurrentHotelName } from 'accessors'
import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import { SecondarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Table, {
  Cell,
  Row,
  TitleCell,
  TitleRow
} from 'components/ReusableComponents/Table'
import InvoiceTable from 'features/InvoicedBookings/InvoiceTable'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'
import billingApi from 'services/api/billing'
import { formatPrice } from 'utils/common'

import styles from './InvoicedBookings.module.scss'

const formatDate = date => moment(date).format('MMM D')

const analyticCardColors = {
  ceruleanBlue: '#2B6CB0',
  successGreen: '#3AAF71',
  pinkishRed: '#FB7185'
}

const InvoicedBookings = ({ prevPage = 'roi' }) => {
  const { invoiceId } = useParams()
  const [conversions, setConversions] = useState()
  const [invoiceDate, setInvoiceDate] = useState()
  const [consumedBookingsCount, setConsumedBookingsCount] = useState(0)
  const [cancellationNoShowCount, setCancellationNoShowCount] = useState(0)
  const [totalBookingCount, setTotalBookingCount] = useState(0)

  const [activeTab, setActiveTab] = useState('all')

  const [isLoading, setIsLoading] = useState(true)

  const currentHotelName = useSelector(selectCurrentHotelName)

  const history = useHistory()

  function getGoBackText() {
    if (prevPage === 'roi') {
      return 'Back to ROI'
    } else if (prevPage === 'invoice') {
      return 'Back to Invoice'
    } else {
      return 'Close'
    }
  }

  function goBack() {
    if (prevPage === 'roi') {
      history.push(`/roi`)
    } else if (prevPage === 'invoice') {
      history.push(`/invoice-preview/${invoiceId}`)
    } else {
      history.push(`/`)
    }
  }

  function filterForActiveTab(bookings) {
    if (activeTab === 'all') {
      return bookings
    } else if (activeTab === 'direct') {
      return bookings.filter(booking => !booking.isAgencyBooking)
    } else if (activeTab === 'ota') {
      return bookings.filter(booking => booking.isAgencyBooking)
    }
  }

  useEffect(() => {
    billingApi.invoicedBookings({ invoiceId }).then(response => {
      setConversions(response.conversions)
      setInvoiceDate(response.invoiceDate)
      setConsumedBookingsCount(response.consumedBookingsCount)
      setCancellationNoShowCount(response.cancellationNoShowCount)
      setTotalBookingCount(
        response.consumedBookingsCount + response.cancellationNoShowCount
      )
      setIsLoading(false)
    })
  }, [invoiceId])

  if (isLoading) {
    return <LoadingIndicator />
  }

  return (
    <ConfirmationDialog
      maxWidth={1000}
      title={`Bookings of ${currentHotelName} for ${moment(invoiceDate).format(
        'MMM, YYYY'
      )}`}
      contentPadding={0}
      negativeMessage={getGoBackText()}
      hasConfirmButton={false}
      onClose={() => goBack()}
    >
      {/* <div className={styles.funnelWrapper}>
        <div className={styles.labels}>
          <div
            className={styles.label}
            style={{ backgroundColor: analyticCardColors.ceruleanBlue }}
          >
            <p className={styles.labelValue}>{totalBookingCount}</p> total bookings
          </div>
          <div
            className={styles.label}
            style={{ backgroundColor: analyticCardColors.successGreen }}
          >
            <p className={styles.labelValue}>{consumedBookingsCount}</p>
            consumed bookings
          </div>
          <div
            className={styles.label}
            style={{ backgroundColor: analyticCardColors.pinkishRed }}
          >
            <p className={styles.labelValue}>{cancellationNoShowCount}</p>cancellations &
            no-shows
          </div>
        </div>
      </div> */}
      <div className={styles.wrapper}></div>
      <div className={styles.bookingsWrapper}>
        <div className={styles.titleHolder}>
          <div className={styles.tabs}>
            <button
              className={activeTab === 'all' ? styles.activeTab : styles.tab}
              onClick={() => setActiveTab('all')}
            >
              All bookings ({conversions.length})
            </button>
            <button
              className={activeTab === 'direct' ? styles.activeTab : styles.tab}
              onClick={() => setActiveTab('direct')}
            >
              Direct bookings ({conversions.filter(b => !b.isAgencyBooking).length})
            </button>
            <button
              className={activeTab === 'ota' ? styles.activeTab : styles.tab}
              onClick={() => setActiveTab('ota')}
            >
              OTA bookings ({conversions.filter(b => b.isAgencyBooking).length})
            </button>
          </div>
        </div>
        <div className={styles.tableHolder}>
          <Table widths={[80, 160, 60, 60, 60, 60, 80, 120, 80, 80]}>
            <TitleRow className={styles.titleRow}>
              <TitleCell>Conf #</TitleCell>
              <TitleCell>Guest name</TitleCell>
              <TitleCell>Invited</TitleCell>
              <TitleCell>Booked</TitleCell>
              <TitleCell>C/I</TitleCell>
              <TitleCell>C/O</TitleCell>
              <TitleCell>Rate code</TitleCell>
              <TitleCell>Past stays (12 mo)</TitleCell>
              <TitleCell>ADR</TitleCell>
              <TitleCell>REV</TitleCell>
            </TitleRow>
            {filterForActiveTab(conversions).map(b => (
              <Row className={styles.row} key={b.bookingId}>
                <Cell overflow="hidden">
                  <span style={{ paddingRight: 10 }} title={b.convertedConfirmationCode}>
                    {b.convertedConfirmationCode}
                  </span>
                </Cell>
                <Cell>{b.convertedClientName}</Cell>
                <Cell>{formatDate(b.invitedOn)}</Cell>
                <Cell>{formatDate(b.convertedOn)}</Cell>
                <Cell>{formatDate(b.checkIn)}</Cell>
                <Cell>{formatDate(b.checkOut)}</Cell>
                <Cell overflow={'hidden'}>
                  <span title={b.convertedRateCode}>{b.convertedRateCode}</span>
                </Cell>
                <Cell>{b.numberOfPriorsByName}</Cell>
                <Cell>
                  {formatPrice(
                    b.convertedTotal /
                      Math.max(moment(b.checkOut).diff(b.checkIn, 'days'), 1)
                  )}
                </Cell>
                <Cell>{formatPrice(b.convertedTotal)}</Cell>
              </Row>
            ))}
          </Table>
        </div>
      </div>
    </ConfirmationDialog>
  )
}

export default InvoicedBookings
